import React, { useState, useEffect, useCallback } from 'react';
import { BetItem } from './BettingSlipDrawer';
import { getLiveEvents, getPreEvents } from '../api/api';
import { EventSnapshot, Market, Outcome } from '../api/types';
import MatchDetailPage from './MatchDetailPage';

const SPORT_EMOJI: Record<string, string> = {
  Soccer: '⚽',
  Basketball: '🏀',
  Tennis: '🎾',
  'Ice Hockey': '🏒',
  Handball: '🤾',
  Volleyball: '🏐',
  Baseball: '⚾',
  'American Football': '🏈',
  'Table Tennis': '🏓',
  eSoccer: '🎮',
  eBasketball: '🎮',
};

const BET_FILTERS = ['Tipp', 'Über/Unter', 'Handicap'];
const UPCOMING_FILTERS = ['Tipp', 'Über/Unter', 'Handicap', 'Beide Teams treffen'];

function find1x2Market(markets: Market[]): { home: Outcome; draw: Outcome; away: Outcome; market: Market } | null {
  const m = markets.find(
    (mk) => mk.market_id === '1' && mk.outcomes.length >= 3 && mk.status !== -1
  );
  if (!m) return null;
  const home = m.outcomes.find((o) => o.outcome_id === '1');
  const draw = m.outcomes.find((o) => o.outcome_id === '2');
  const away = m.outcomes.find((o) => o.outcome_id === '3');
  if (!home || !draw || !away) return null;
  return { home, draw, away, market: m };
}

function countOutcomes(markets: Market[]): number {
  return markets.reduce((acc, m) => acc + m.outcomes.length, 0);
}

function formatOdds(odds: string): string {
  return parseFloat(odds).toFixed(2).replace('.', ',');
}

function formatClock(ev: EventSnapshot): string {
  if (ev.status?.clock) return ev.status.clock.split(':')[0] + "'";
  return 'Live';
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

interface LivePageProps {
  bets: BetItem[];
  onAddBet: (bet: BetItem) => void;
  onRemoveBet: (id: string) => void;
  onLoginClick?: () => void;
}

const LivePage: React.FC<LivePageProps> = ({ bets, onAddBet, onRemoveBet, onLoginClick }) => {
  const [liveEvents, setLiveEvents] = useState<EventSnapshot[]>([]);
  const [preEvents, setPreEvents] = useState<EventSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSport, setActiveSport] = useState('Soccer');
  const [activeBetFilter, setActiveBetFilter] = useState('Tipp');
  const [upcomingFilter, setUpcomingFilter] = useState('Tipp');
  const [selectedEvent, setSelectedEvent] = useState<EventSnapshot | null>(null);

  const loadData = useCallback(async () => {
    try {
      const [live, pre] = await Promise.all([getLiveEvents(), getPreEvents({ hours: 6 })]);
      setLiveEvents(live.items);
      setPreEvents(pre.items);
    } catch (e) {
      console.error('Failed to load events', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const sportCounts: Record<string, number> = {};
  for (const ev of liveEvents) {
    const s = ev.event.sport_name;
    sportCounts[s] = (sportCounts[s] || 0) + 1;
  }
  const sportTabs = Object.entries(sportCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name, count]) => ({ name, count, emoji: SPORT_EMOJI[name] || '🏅' }));

  const filteredLive = liveEvents.filter((ev) => ev.event.sport_name === activeSport);
  const filteredPre = preEvents.filter((ev) => ev.event.sport_name === activeSport).slice(0, 10);

  const isSelected = (eventId: string, outcomeId: string) =>
    bets.some((b) => b.id === `${eventId}-${outcomeId}`);

  const handleOddsClick = (
    ev: EventSnapshot,
    market: Market,
    outcome: Outcome,
    tippLabel: string
  ) => {
    const id = `${ev.event_id}-${outcome.outcome_id}`;
    if (isSelected(ev.event_id, outcome.outcome_id)) {
      onRemoveBet(id);
    } else {
      const existing = bets.find((b) => b.id.startsWith(ev.event_id));
      if (existing) onRemoveBet(existing.id);

      onAddBet({
        id,
        matchId: 0,
        matchName: `${ev.event.home.name} - ${ev.event.away.name}`,
        betType: tippLabel === 'Heimsieg' ? 'home' : tippLabel === 'Unentschieden' ? 'draw' : 'away',
        tippLabel,
        odds: formatOdds(outcome.odds),
        eventId: ev.event_id,
        marketId: market.market_id,
        outcomeId: outcome.outcome_id,
        specifiers: market.specifiers
          ? Object.entries(market.specifiers).map(([k, v]) => `${k}=${v}`).join('|')
          : '',
      });
    }
  };

  if (selectedEvent) {
    return (
      <MatchDetailPage
        event={selectedEvent}
        onBack={() => setSelectedEvent(null)}
        bets={bets}
        onAddBet={onAddBet}
        onRemoveBet={onRemoveBet}
        onLoginClick={onLoginClick ?? (() => {})}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen pb-20">
      {sportTabs.length > 0 && (
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex gap-6 overflow-x-auto">
          {sportTabs.map((sport) => (
            <button
              key={sport.name}
              onClick={() => setActiveSport(sport.name)}
              className={`flex flex-col items-center gap-1 min-w-[56px] pb-1 border-b-2 transition ${
                activeSport === sport.name
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-gray-500'
              }`}
            >
              <div className="relative">
                <span className="text-2xl">{sport.emoji}</span>
                <span className="absolute -top-1 -right-3 text-xs font-bold text-gray-700">{sport.count}</span>
              </div>
              <span className="text-xs font-medium whitespace-nowrap">{sport.name}</span>
            </button>
          ))}
        </div>
      )}

      <div className="px-3 pt-3">
        <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-1">
          <button className="flex-shrink-0 p-2 border border-gray-300 rounded-full bg-white">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
            </svg>
          </button>
          {BET_FILTERS.map((filter) => (
            <button
              key={filter}
              onClick={() => setActiveBetFilter(filter)}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition ${
                activeBetFilter === filter
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-700 border-gray-300'
              }`}
            >
              {filter}
            </button>
          ))}
        </div>

        {filteredLive.length === 0 ? (
          <div className="bg-white rounded-xl p-6 text-center text-gray-400 text-sm mb-4">
            Bu spor için canlı maç yok.
          </div>
        ) : (
          <div className="bg-white rounded-xl overflow-hidden shadow-sm mb-4">
            {filteredLive.map((ev, idx) => {
              const odds3way = find1x2Market(ev.markets);
              const total = countOutcomes(ev.markets);
              const redHome = (ev.status?.statistics?.red_cards?.home || 0) > 0;
              const redAway = (ev.status?.statistics?.red_cards?.away || 0) > 0;

              return (
                <div key={ev.event_id}>
                  {idx > 0 && <div className="border-t border-gray-100 mx-4" />}
                  <div className="px-4 py-3">
                    <div className="flex gap-3 mb-3 cursor-pointer" onClick={() => setSelectedEvent(ev)}>
                      <div className="text-left min-w-[30px]">
                        <div className="text-xs font-bold text-red-600">{formatClock(ev)}</div>
                        <div className="text-red-400 text-xs">●</div>
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <div className="flex items-center gap-1">
                            <span className="text-sm font-medium text-gray-800 truncate max-w-[160px]">{ev.event.home.name}</span>
                            {redHome && <span className="w-3 h-4 bg-red-600 rounded-sm inline-block ml-1" />}
                          </div>
                          <span className="text-sm font-bold text-gray-800 ml-2">{ev.status?.home_score ?? '-'}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-1">
                            <span className="text-sm font-medium text-gray-800 truncate max-w-[160px]">{ev.event.away.name}</span>
                            {redAway && <span className="w-3 h-4 bg-red-600 rounded-sm inline-block ml-1" />}
                          </div>
                          <span className="text-sm font-bold text-gray-800 ml-2">{ev.status?.away_score ?? '-'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 items-center">
                      {odds3way ? (
                        <>
                          {([
                            { label: '1', outcome: odds3way.home, tipp: 'Heimsieg' },
                            { label: 'X', outcome: odds3way.draw, tipp: 'Unentschieden' },
                            { label: '2', outcome: odds3way.away, tipp: 'Auswärtssieg' },
                          ] as const).map(({ label, outcome, tipp }) => {
                            const sel = isSelected(ev.event_id, outcome.outcome_id);
                            return (
                              <button
                                key={label}
                                onClick={() => handleOddsClick(ev, odds3way.market, outcome, tipp)}
                                className={`flex-1 flex items-center justify-between rounded-lg px-3 py-2 transition ${
                                  sel ? 'bg-gray-800' : 'bg-gray-100 hover:bg-gray-200'
                                }`}
                              >
                                <span className={`text-xs ${sel ? 'text-gray-300' : 'text-gray-500'}`}>{label}</span>
                                <span className={`text-sm font-bold ${sel ? 'text-white' : 'text-gray-800'}`}>{formatOdds(outcome.odds)}</span>
                              </button>
                            );
                          })}
                          <button className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap ml-1">
                            {total} <span>›</span>
                          </button>
                        </>
                      ) : (
                        <>
                          {[0, 1, 2].map((i) => (
                            <div key={i} className="flex-1 flex items-center justify-center bg-gray-100 rounded-lg px-3 py-2">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                              </svg>
                            </div>
                          ))}
                          <button className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap ml-1">
                            {total} <span>›</span>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {filteredPre.length > 0 && (
          <>
            <h2 className="text-lg font-bold text-gray-900 mb-3">Nächste 6 Stunden</h2>
            <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
              {UPCOMING_FILTERS.map((f) => (
                <button
                  key={f}
                  onClick={() => setUpcomingFilter(f)}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition ${
                    upcomingFilter === f
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-700 border-gray-300'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-3">
              {filteredPre.map((ev) => {
                const odds3way = find1x2Market(ev.markets);
                const total = countOutcomes(ev.markets);
                return (
                  <div key={ev.event_id} className="bg-white rounded-xl shadow-sm px-4 py-3">
                    <div className="flex gap-3 items-start mb-2 cursor-pointer" onClick={() => setSelectedEvent(ev)}>
                      <div className="text-left min-w-[40px]">
                        <div className="text-xs text-gray-500">Heute</div>
                        <div className="text-xs font-bold text-gray-700">{formatTime(ev.event.scheduled_at)}</div>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800 truncate">{ev.event.home.name}</p>
                        <p className="text-sm font-medium text-gray-800 truncate">{ev.event.away.name}</p>
                      </div>
                    </div>
                    {odds3way ? (
                      <div className="flex gap-2 items-center">
                        {([
                          { label: '1', outcome: odds3way.home, tipp: 'Heimsieg' },
                          { label: 'X', outcome: odds3way.draw, tipp: 'Unentschieden' },
                          { label: '2', outcome: odds3way.away, tipp: 'Auswärtssieg' },
                        ] as const).map(({ label, outcome, tipp }) => {
                          const sel = isSelected(ev.event_id, outcome.outcome_id);
                          return (
                            <button
                              key={label}
                              onClick={() => handleOddsClick(ev, odds3way.market, outcome, tipp)}
                              className={`flex-1 flex items-center justify-between rounded-lg px-3 py-2 transition ${
                                sel ? 'bg-gray-800' : 'bg-gray-100 hover:bg-gray-200'
                              }`}
                            >
                              <span className={`text-xs ${sel ? 'text-gray-300' : 'text-gray-500'}`}>{label}</span>
                              <span className={`text-sm font-bold ${sel ? 'text-white' : 'text-gray-800'}`}>{formatOdds(outcome.odds)}</span>
                            </button>
                          );
                        })}
                        <button className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap ml-1">
                          {total} <span>›</span>
                        </button>
                      </div>
                    ) : (
                      <div className="text-xs text-gray-400 mt-1">Oranlar yükleniyor...</div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LivePage;
